import { Span, trace } from '@opentelemetry/api';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { getCartLineItemProperty } from '@/utils/shopify';
import { CartItemProperty } from '@/core/constants';
import { MoneyVariant } from './shopify/types';
import { formatMoney, parseNumber } from './shopify/utils';
import { isServerSide } from '@/utils/environment';

const accessToken = process.env.SHOPIFY_ACCESS_TOKEN;
const storeDomain = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN;
const apiVersion = '2025-07';
const tracer = trace.getTracer('shopify');

let client: ReturnType<typeof createStorefrontApiClient> | null = null;

function getClient(): ReturnType<typeof createStorefrontApiClient> {
  if (client) {
    return client;
  }

  if (isServerSide()) {
    client = createStorefrontApiClient({
      storeDomain: storeDomain as string,
      apiVersion,
      publicAccessToken: accessToken,
      customFetchApi: fetch,
      retries: 2,
    });
  } else {
    throw new Error(
      `Shopify API client should not be initialized in browser environment. Use API routes instead.`,
    );
  }

  return client;
}

function traceRequest(fun: (span: Span) => Promise<any>) {
  return tracer.startActiveSpan('shopify.request', async (span) => {
    try {
      const response = await fun(span);
      return response;
    } finally {
      span.end();
    }
  });
}

async function shopifyRequest(
  query: string,
  variables: Record<string, any> = {},
  options: ShopifyRequestOptions = {},
): Promise<any> {
  return traceRequest(async (span) => {
    span.setAttribute('graphql.query', query);
    span.setAttribute('graphql.variables', JSON.stringify(variables));

    const response = await getClient().request(query, {
      variables,
      ...(options.ipAddress && {
        headers: {
          'Shopify-Storefront-Buyer-IP': options.ipAddress,
        },
      }),
    });

    return response.data;
  });
}

function decodeId(id: string) {
  return Buffer.from(id, 'base64').toString();
}

export function isGlobalId(id: string) {
  return id.startsWith('gid://');
}

export function cartIdToToken(id: string) {
  return id.replace('gid://shopify/Cart/', '');
}

export function cartTokenToId(token: string) {
  return `gid://shopify/Cart/${token}`;
}

const GET_CART_QUERY = `
  query GetCart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
      lines(first: 50) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
              }
            }
            attributes {
              key
              value
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  }
`;

export interface Cart {
  id: string;
  totalQuantity: number;
  lines: CartLines;
}

interface ShopifyRequestOptions {
  ipAddress?: string;
}

export interface CartAttribute {
  key: string;
  value: string;
}

export interface Merchandise {
  id: string;
}

export interface CartLineCost {
  subtotalAmount: MoneyVariant;
}

export interface CartLineNode {
  id: string;
  quantity: number;
  merchandise: Merchandise;
  cost: CartLineCost;
  attributes: CartAttribute[];
}

export interface CartLineEdge {
  node: CartLineNode;
}

export interface CartLines {
  edges: CartLineEdge[];
}

export interface CartLineInput {
  merchandiseId: string;
  quantity: number;
  attributes?: CartAttribute[] | undefined;
}

export async function getCartById(
  id: string,
  options: ShopifyRequestOptions = {},
): Promise<Cart> {
  const response = await shopifyRequest(GET_CART_QUERY, { id }, options);
  return response.cart;
}

const ADD_TO_CART_MUTATION = `
  mutation AddToCart($cartId: ID!, $cartLines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $cartLines) {
      cart {
        id
        totalQuantity
      }
    }
  }
`;

export async function addToCart(
  cartId: string,
  lines: CartLineInput[],
  options: ShopifyRequestOptions = {},
) {
  const cartLines = [...lines];

  return await shopifyRequest(
    ADD_TO_CART_MUTATION,
    {
      cartId,
      cartLines,
    },
    options,
  );
}

const REMOVE_FROM_CART_MUTATION = `
  mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        totalQuantity
      }
    }
  }
`;

export async function removeFromCart(
  cartId: string,
  lineIds: string[],
  options: ShopifyRequestOptions = {},
) {
  return await shopifyRequest(
    REMOVE_FROM_CART_MUTATION,
    {
      cartId,
      lineIds,
    },
    options,
  );
}

const CREATE_CART_MUTATION = `
  mutation CreateCart {
    cartCreate {
      cart {
        id
        totalQuantity
      }
    }
  }
`;

export interface CartCreate {
  cartCreate: {
    cart: Cart;
  };
}

export async function createCart(
  options: ShopifyRequestOptions = {},
): Promise<CartCreate> {
  return await shopifyRequest(CREATE_CART_MUTATION, {}, options);
}

// https://shopify.dev/docs/api/storefront/2023-07/objects/Product
// https://shopify.dev/docs/api/storefront/2023-07/objects/ProductVariant
// Note that fetching quantityAvailable would require elevated access.
const GET_PRODUCT_QUERY = `
  query GetProduct($id: ID!) {
    product(id: $id) {
      id
      title
      handle
      productType
      tags
      variants(first: 50) {
        edges {
          node {
            id
            title
            availableForSale
            currentlyNotInStock
            sku
            image {
              altText
              url
            }
            compareAtPrice {
              amount
              currencyCode
            }
            price {
              amount
              currencyCode
            }
          }
        }
      }
      metafieldPriceStandalone: metafield(
        namespace: "global"
        key: "price_cents_standalone"
      ) {
        key
        value
        type
      }
      metafieldPriceBabylist: metafield(
        namespace: "global"
        key: "price_cents_babylist"
      ) {
        key
        value
        type
      }
    }
  }
`;

export interface Node<T> {
  id: string;
  node: T;
}

export interface Metafield {
  key: string;
  value: string;
  type: string;
}

export interface ShopifyProductResponse {
  product: {
    id: string;
    title: string;
    handle: string;
    productType: string;
    tags: string[];
    variants: {
      edges: Node<ShopifyVariant>[];
    };
    metafieldPriceStandalone: Metafield | null;
    metafieldPriceBabylist: Metafield | null;
  };
}

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  productType: string;
  tags: string[];
  variants: ShopifyVariant[];
}

export interface ImageVariant {
  altText: string;
  url: string;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  currentlyNotInStock: boolean;
  sku: string;
  compareAtPrice: MoneyVariant | null;
  formattedPrice: string | null;
  price: MoneyVariant | null;
  image: ImageVariant;
  priceCentsStandalone?: number;
  priceCentsBabylist?: number;
}

export enum ShopifyProductType {
  COURSE = 'Course Pack',
  PLAY_KITS = 'Play Kits',
  SKILL_SET = 'Skill Set',
  EARLY_READER_CLUB = 'ERC',
  KIT_COMPONENT = 'Kit Component',
}

export interface ShopifyCart {
  token: string;
  note: string | null;
  attributes: Record<string, string | number | boolean | null>;
  original_total_price: number;
  total_price: number;
  total_discount: number;
  total_weight: number;
  item_count: number;
  items: ShopifyCartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  cart_level_discount_applications: DiscountApplication[];
}

export interface ShopifyCartItem {
  id: number;
  properties?: Record<string, string | null>;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  total_discount: number;
  discounts: DiscountApplication[];
  sku: string;
  grams: number;
  vendor: string;
  taxable: boolean;
  product_id: number;
  product_type: string;
  gift_card: boolean;
  url: string;
  featured_image?: ShopifyImage;
  handle: string;
  requires_shipping: boolean;
  product_title: string;
  product_description: string;
  variant_title?: string;
  variant_options: string[];
  options_with_values: OptionWithValue[];
}

interface ShopifyImage {
  src: string;
  alt?: string;
}

interface OptionWithValue {
  name: string;
  value: string;
}

interface DiscountApplication {
  title: string;
  description: string;
  value: number;
  type: 'percentage' | 'fixed_amount';
  target_type: 'line_item' | 'shipping_line';
}

export function transformResponseToProduct(
  response: ShopifyProductResponse,
  locale?: string,
): ShopifyProduct | null {
  const product = response.product;
  if (!product || !product.id) return null;

  const alternativePrices: Record<string, number> = {};

  const priceStandalone = parseNumber(product.metafieldPriceStandalone?.value);
  if (priceStandalone) {
    alternativePrices.priceCentsStandalone = priceStandalone;
  }

  const priceBabylist = parseNumber(product.metafieldPriceBabylist?.value);
  if (priceBabylist) {
    alternativePrices.priceCentsBabylist = priceBabylist;
  }

  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    productType: product.productType,
    tags: product.tags,
    variants: product.variants.edges.map(
      (variant): ShopifyVariant => ({
        id: variant.node.id,
        title: variant.node.title,
        availableForSale: variant.node.availableForSale,
        currentlyNotInStock: variant.node.currentlyNotInStock,
        sku: variant.node.sku,
        formattedPrice: formatMoney(variant.node.price, locale),
        compareAtPrice: variant.node.compareAtPrice,
        price: variant.node.price,
        image: variant.node.image,
        ...alternativePrices,
      }),
    ),
  };
}

export async function getProductById(
  id: string,
  locale?: string,
): Promise<ShopifyProduct | null> {
  if (!id) {
    return null;
  }
  const decodedId = isGlobalId(id) ? id : decodeId(id);
  const response = await shopifyRequest(GET_PRODUCT_QUERY, { id: decodedId });
  const product = transformResponseToProduct(
    { product: response.product },
    locale,
  );

  return product;
}

export const PRE_SALE_SHOPIFY_TAG = 'pre-order';

export enum InventoryStatusCode {
  // There is available inventory for this variant.
  ACTIVE = 'active',
  // There is no available inventory for this variant and the "Continue selling when out of stock" box is unchecked for the variant.
  OUT_OF_STOCK = 'out_of_stock',
  // There is no available inventory for this variant but the "Continue selling when out of stock" box is checked for the variant and the product has the pre-order tag.
  PRESALE = 'presale',
  // There is no available inventory for this variant but the "Continue selling when out of stock" box is checked for the variant and the product does not have the pre-order tag.
  BACKORDER = 'backorder',
}

export function getProductInventoryStatus(
  product: ShopifyProduct | null,
  // The selected variant can only be omitted for products which do not have variants. Shopify seems to provide a single dummy variant for such products. Otherwise, the function will return false positives/negatives for all variants but the first one.
  selectedVariant?: ShopifyVariant,
): InventoryStatusCode | null {
  if (!product) {
    // That's not good.
    return null;
  }

  const { variants, tags } = product;

  let productVariant = undefined;
  if (selectedVariant) {
    productVariant = variants.find(
      (variant) => variant.id === selectedVariant.id,
    );
  } else if (variants.length > 0) {
    // When not provided a selected variant, grab the first one.
    productVariant = variants[0];
  }

  if (!productVariant) {
    console.warn(
      'Product variant not found. Could not determine inventory status.',
    );
    return null;
  }

  const { availableForSale, currentlyNotInStock } = productVariant;

  // No inventory available for the variant and the "Continue selling when out of stock" box is unchecked for the variant.
  if (!availableForSale) {
    return InventoryStatusCode.OUT_OF_STOCK;
  }

  // No inventory available for the variant but the "Continue selling when out of stock" box is checked for the variant.
  if (currentlyNotInStock) {
    // The product has the pre-order tag.
    const hasPreSaleTag = tags.find((tag) => tag === PRE_SALE_SHOPIFY_TAG);
    if (hasPreSaleTag) {
      return InventoryStatusCode.PRESALE;
    }

    return InventoryStatusCode.BACKORDER;
  }

  // Inventory is available for the variant. Wonderful.
  return InventoryStatusCode.ACTIVE;
}

export function removeQuantityLimitedProducts(
  cart: Cart | undefined,
  lineItem: CartLineInput,
) {
  // if cart is unavailable, return true to allow all items to remain in cart line items
  if (!cart) return true;

  const cartItem = cart.lines?.edges?.find(
    (currentCartLineItem) =>
      currentCartLineItem.node.merchandise.id === lineItem.merchandiseId,
  );

  // if item is not in the cart, let it stay in cart lines input array to get added
  if (!cartItem) return true;

  const isQuantityLimitReached =
    getCartLineItemProperty(
      CartItemProperty.QuantityLimitedToOne,
      cartItem?.node?.attributes,
    ) === 'true';

  // if item is already in the cart and has reached the quantity limit
  // remove it from cart lines input, so it does not get added again
  return !(cartItem && isQuantityLimitReached);
}
